import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable, catchError, map, of } from 'rxjs'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
  FlxSelectComponent,
} from '@flowx/angular-ui-toolkit'
import { ApiTheme } from '@flowx/designer/modules/cms/pages/themes/theme.dictionary'
import {
  type Slice,
  inputSliceItemsOf,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import type { DataModelUiEntity } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { computeDataModelTree } from '@flowx/designer/modules/data-model-builder/services/data-model-builder.utils'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import { FlxTableDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { DataModelApiService } from '@flowx/designer/shared/api/data-model.api'
import { UiFlowsApiService } from '@flowx/designer/shared/api/ui-flows.api'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'
import {
  getSelectedNodesAndSlice,
  toDataModelUiEntities,
  unflattenObject,
} from '@flowx/designer/shared/entities/data-model.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  selectProjectDataModelSystemEntities,
  selectProjectDataModelTypes,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { selectRouteParams } from '@flowx/designer/shared/store/selectors/router.selectors'

/** What the run dialog opens with: the UI Flow, and the theme, locale and language it starts in. */
export interface UiFlowRunDialogData {
  themeId: string
  themes: ApiTheme[]
  locale: string
  language: string
  uiFlowId: string
  uiFlowName: string
}

@Component({
  selector: 'flx-ui-flow-run-dialog',
  imports: [
    FlxModalModule,
    FlxFormControlModule,
    ReactiveFormsModule,
    FlxButtonModule,
    FlxSelectComponent,
    CodeEditorComponent,
  ],
  template: `
    <flx-modal
      [dismissible]="true"
      (dismiss)="closeModal()"
      class="h-full flex flex-col flx-ds"
    >
      <div
        class="flx-modal-title"
        flxModalHeaderTitle
      >
        Run Ui Flow
      </div>
      <div
        class="flx-modal-subtitle"
        flxModalHeaderSubtitle
        data-testid="modal-header-subtitle"
      >
        {{ uiFlowName() }}
      </div>

      <div class="flex flex-col gap-3 mb-8">
        <div class="h-10">
          <p class="text-neutral-900 text-sm font-semibold">Input Parameters</p>
          <p class="text-xs text-neutral-500">Variables expected by the UI Flow</p>
        </div>
        <div class="flex flex-col w-full h-[320px]">
          <flx-code-editor
            [options]="editorOptions"
            [formControl]="startParamsFC"
            [forceShowErrors]="true"
            class="h-full"
          />
        </div>
      </div>

      <flx-select
        [formControl]="themeFC"
        class="w-full mb-8"
        data-testId="theme-selector"
      >
        <flx-label>Theme</flx-label>
        <i
          class="ph ph-palette leading-4 text-base text-neutral-500"
          flxPrefixIcon
        ></i>

        @for (theme of this.themes(); track theme.flowxUuid) {
          <flx-select-option
            [value]="theme.flowxUuid"
            [attr.data-testId]="theme.name"
          >
            {{ theme.name }}
          </flx-select-option>
        }
      </flx-select>

      <div class="flex flex-row justify-end items-center">
        <button
          [disabled]="startParamsFC.invalid"
          (click)="runUiFlow()"
          data-testId="run-ui-flow-button"
          flxButton
        >
          Run
        </button>
      </div>
    </flx-modal>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxUiFlowRunDialogComponent implements OnInit {
  #flxModalService = inject(FlxModalService)
  #router = inject(Router)
  #data = inject<UiFlowRunDialogData>(FLX_MODAL_DATA)
  #ngrxStore = inject(Store)
  #flxDateFormatPipe = inject(FlxTableDateFormatPipe)
  #dataModelApi = inject(DataModelApiService)
  #uiFlowsApi = inject(UiFlowsApiService)
  #toastService = inject(ToastMessageService)
  #params = this.#ngrxStore.selectSignal(selectRouteParams)()
  #projectDataTypes = this.#ngrxStore.selectSignal(selectProjectDataModelTypes)
  #projectSystemEntities = this.#ngrxStore.selectSignal(selectProjectDataModelSystemEntities)

  // Loaded here, not into the store's data model: every open editor reads that one, and loading the
  // UI Flow there would show its data model on the page under the dialog.
  #uiFlowEntities = toSignal(this.#loadUiFlowEntities(), { initialValue: null })

  uiFlowDataModelTree = computed(() => {
    const uiFlowEntities = this.#uiFlowEntities()

    return uiFlowEntities
      ? computeDataModelTree(
          [...uiFlowEntities, ...this.#projectDataTypes(), ...this.#projectSystemEntities()],
          null,
          false
        )
      : null
  })

  uiFlowInputSlice = toSignal(this.#loadInputSliceItems(), { initialValue: [] })

  uiFlowName = signal<string | null>(null)
  themes = signal<ApiTheme[]>([])
  themeFC = new FormControl<string>(null)
  startParamsFC = new FormControl<string>('{}', [
    Validators.required,
    this.#jsonValidator.bind(this),
  ])

  readonly editorOptions = {
    language: 'json',
    autoIndent: 'full',
    formatOnPaste: true,
    formatOnType: true,
    automaticLayout: true,
    glyphMargin: false,
    lineNumbersMinChars: 0,
    quickSuggestions: false,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
  }

  constructor() {
    effect(() => {
      const dataModelTree = this.uiFlowDataModelTree()
      const slices = this.uiFlowInputSlice()
      if (dataModelTree && slices?.length) {
        this.startParamsFC.setValue(
          JSON.stringify(
            unflattenObject(
              getSelectedNodesAndSlice(
                dataModelTree.tree || [],
                slices,
                [],
                false,
                true,
                (date: string) => this.#flxDateFormatPipe.transform(date, false),
                true
              ).exampleValues
            ),
            null,
            2
          )
        )
      }
    })
  }

  ngOnInit(): void {
    this.uiFlowName.set(this.#data.uiFlowName)
    this.themes.set(this.#data.themes)
    this.themeFC.setValue(this.#data.themeId)
  }

  closeModal(): void {
    this.#flxModalService.hide()
  }

  runUiFlow(): void {
    const { appId, appVersionId, workspaceId } = this.#params

    this.#router.navigate(
      [
        'guard',
        'workspace',
        workspaceId,
        'projects',
        appId,
        'runtime',
        appVersionId,
        'ui-flow',
        this.#data.uiFlowId,
        'instance',
      ],
      {
        queryParams: {
          themeId: this.themeFC.value,
          locale: this.#data.locale,
          language: this.#data.language,
        },
        state: {
          startParams: this.#parseStartParams(),
        },
      }
    )

    this.closeModal()
  }

  #jsonValidator(control: AbstractControl<string>): ValidationErrors | null {
    const value = control.value?.trim()
    if (!value) {
      return { required: true }
    }

    try {
      JSON.parse(value)
      return null
    } catch {
      return { invalidJson: true }
    }
  }

  #parseStartParams(): Record<string, unknown> {
    try {
      const parsed = JSON.parse(this.startParamsFC.value?.trim() || '{}')
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  /** The UI Flow's own data model entities, as the editors' data model effect maps them. */
  #loadUiFlowEntities(): Observable<DataModelUiEntity[] | null> {
    const uiFlowId = this.#data.uiFlowId

    if (!uiFlowId) {
      return of(null)
    }

    return this.#dataModelApi
      .getDataModel({ resourceType: ApplicationResourceType.UiFlow, resourceId: uiFlowId })
      .pipe(
        map((dataModel) => toDataModelUiEntities(dataModel, ApplicationResourceType.UiFlow)),
        catchError(() => {
          this.#toastService.showErrorMessage('Load data model types failed')
          return of(null)
        })
      )
  }

  /** The fields of the UI Flow's input slice, which its start parameters are built from. */
  #loadInputSliceItems(): Observable<Slice[]> {
    const uiFlowId = this.#data.uiFlowId

    if (!uiFlowId) {
      return of([])
    }

    return this.#uiFlowsApi.getDataSlices(uiFlowId).pipe(
      map((slices) => inputSliceItemsOf(mapResource(slices))),
      catchError(() => {
        this.#toastService.showErrorMessage('Error fetching slices!')
        return of([])
      })
    )
  }
}
